// error_boundary.jsx — keeps one bad screen (or one malformed record) from
// blanking the whole app, plus the data-safety notices (storage full, unreadable
// saved data). Every way out keeps the user's data: download it, undo the last
// restore, go Home, or reload.

import React from 'react';
import { LL, LLIcon } from './tokens.jsx';
import { useApp, LS_KEY, rescueBackupJSON } from './store.jsx';
import { undoInfo, undoRestoreOnDisk } from './persist.js';

function downloadText(name, text) {
  try {
    const blob = new Blob([text], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch (e) { console.warn('download failed', e); }
}
const stamp = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
export function downloadRawState(key = LS_KEY, name) {
  let raw = null;
  try { raw = localStorage.getItem(key); } catch (_) {}
  if (raw) downloadText(name || `liftlog-data-${stamp()}.json`, raw);
  return !!raw;
}
// Everything in memory right now (including changes that could not be saved),
// as a normal backup file — restorable with Settings › Import JSON.
export function downloadBackup(json) {
  const text = json || rescueBackupJSON();
  if (text) { downloadText(`liftlog-backup-${stamp()}.json`, text); return true; }
  return downloadRawState();
}

const btn = (primary) => ({
  height: 48, padding: '0 20px', borderRadius: LL.r.md, cursor: 'pointer', width: '100%',
  border: primary ? 'none' : `1px solid ${LL.line2}`,
  background: primary ? LL.accent : 'transparent', color: primary ? LL.accentInk : LL.fg,
  fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600,
});

export class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error('[liftlog] screen crashed', error, info?.componentStack); }
  goHome = () => {
    try { localStorage.setItem('liftlog.route', JSON.stringify({ name: 'home', params: {} })); } catch {}
    window.location.reload();
  };
  undoRestore = async () => {
    // Puts back what was on the device before the restore, keeping workouts
    // finished since (persist.mergeUndo), then reloads.
    try { await undoRestoreOnDisk(); } catch (e) { console.warn('undo restore failed', e); }
    this.goHome();
  };
  render() {
    if (!this.state.error) return this.props.children;
    // Undo is only offered within a day of a restore, and never as the main action.
    let canUndo = false;
    try { const u = undoInfo(); canUndo = !!(u && !u.expired); } catch (_) {}
    // A crash on Home itself would loop forever behind "Back to home": offer a
    // plain reload of the previous screen instead and lead with the data exits.
    let onHome = false;
    try { onHome = (JSON.parse(localStorage.getItem('liftlog.route') || '{}').name || 'home') === 'home'; } catch (_) { onHome = true; }
    return (
      <div style={{ flex: 1, minHeight: '100dvh', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 28, textAlign: 'center', background: LL.bg }}>
        <div style={{ width: 56, height: 56, borderRadius: LL.r.md, background: LL.surface2, color: LL.warn, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
          <LLIcon.flame style={{ width: 26, height: 26 }} />
        </div>
        <div style={{ fontFamily: LL.display, fontSize: LL.fs.stat, fontWeight: 700, color: LL.fg }}>Something went wrong on this screen</div>
        <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, color: LL.fgMute, marginTop: 8, maxWidth: 320, lineHeight: 1.45 }}>
          Your workouts are still saved on this device. {canUndo ? 'If this started right after today’s restore, you can undo it — workouts logged since are kept.' : 'Download a copy of your data, then head back home.'}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%', maxWidth: 320, marginTop: 22 }}>
          <button onClick={onHome ? () => window.location.reload() : this.goHome} style={btn(true)}>{onHome ? 'Reload' : 'Back to home'}</button>
          <button onClick={() => downloadBackup()} style={btn(false)}>Download my data (JSON)</button>
          {canUndo && <button onClick={this.undoRestore} style={btn(false)}>Undo today’s restore</button>}
        </div>
        <div style={{ fontFamily: LL.mono, fontSize: LL.fs.cap, color: LL.fgFaint, marginTop: 18, maxWidth: 320, wordBreak: 'break-word' }}>{String(this.state.error?.message || this.state.error)}</div>
      </div>
    );
  }
}

// Blocking notices for the two ways the device copy can go wrong.
export function DataAlerts() {
  const { storageFull, dismissStorageFull, corruptKey, backupNow } = useApp();
  const [corruptOpen, setCorruptOpen] = React.useState(!!corruptKey);
  if (!storageFull && !corruptOpen) return null;
  const card = (title, body, actions) => (
    <div role="alertdialog" aria-modal="true" style={{ position: 'fixed', inset: 0, zIndex: 400, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div style={{ width: '100%', maxWidth: 520, background: LL.surface, borderTopLeftRadius: LL.r.lg, borderTopRightRadius: LL.r.lg, border: `1px solid ${LL.line}`, borderBottom: 'none', boxShadow: LL.shadow.sheet, padding: '22px 20px calc(env(safe-area-inset-bottom, 0px) + 22px)', boxSizing: 'border-box' }}>
        <div style={{ fontFamily: LL.display, fontSize: LL.fs.h3, fontWeight: 700, color: LL.fg }}>{title}</div>
        <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, color: LL.fgMute, marginTop: 8, lineHeight: 1.5 }}>{body}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 18 }}>{actions}</div>
      </div>
    </div>
  );
  if (storageFull) {
    return card('Device storage is full',
      'Your latest changes are safe in the app but not saved on the phone yet. Download a backup now — it includes them. Then free up space on the phone (apps, photos, other site data); Lift Log keeps trying to save.',
      <>
        <button style={btn(true)} onClick={() => { downloadBackup(backupNow && backupNow()); }}>Download backup</button>
        <button style={btn(false)} onClick={dismissStorageFull}>Continue (changes may be lost)</button>
      </>);
  }
  return card('Your saved data could not be read',
    corruptKey === 'unsaved'
      ? 'The stored data was damaged and there was no room to keep a copy. Lift Log started fresh.'
      : 'The stored data was damaged, so Lift Log started fresh. A copy of the original was kept on this device. Download it and send it to support or restore it from Settings › Import.',
    <>
      {corruptKey && corruptKey !== 'unsaved' && <button style={btn(true)} onClick={() => downloadRawState(corruptKey, `liftlog-unreadable-${stamp()}.json`)}>Download the original</button>}
      <button style={btn(!corruptKey || corruptKey === 'unsaved')} onClick={() => setCorruptOpen(false)}>Continue</button>
    </>);
}
